import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Award, Lock, Share2 } from "lucide-react";

const SEEN = "chatmilu.badges.seen";

// Everything earned, everything still to earn — and a one-tap share the moment one lands.
export const BadgeWall = () => {
  const { user } = useAuth();
  const [badges, setBadges] = useState(null);

  useEffect(() => {
    api.get("/badges/mine").then(({ data }) => {
      const all = data.badges || [];
      setBadges(all);
      const had = JSON.parse(localStorage.getItem(SEEN) || "[]");
      const fresh = all.filter((b) => b.earned && !had.includes(b.key));
      localStorage.setItem(SEEN, JSON.stringify(all.filter((b) => b.earned).map((b) => b.key)));
      if (fresh.length) {
        toast.success(`${fresh[0].name} earned — share it?`, {
          action: { label: "Share", onClick: () => share(fresh[0]) },
          duration: 9000,
        });
      }
    }).catch(() => setBadges([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const share = async (badge) => {
    const link = `${window.location.origin}/signup?ref=${user?.referral_code || ""}`;
    const text = `I just earned the "${badge.name}" badge on ChatMiLu — ${badge.note.toLowerCase()}. `
      + `Come and learn a language by talking to MiLu: ${link}`;
    try {
      if (navigator.share) await navigator.share({ text });
      else {
        await navigator.clipboard.writeText(text);
        toast.success("Copied — your referral link is in there");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not open the share sheet");
    }
  };

  if (!badges?.length) return null;

  return (
    <>
      <h2 className="mt-12 text-base font-semibold md:text-lg">
        Your badges
        <span className="ms-3 text-xs font-normal text-ink-soft">
          {badges.filter((b) => b.earned).length} of {badges.length}
        </span>
      </h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="badge-wall">
        {badges.map((b) => (
          <div key={b.key} data-testid={`badge-${b.key}`}
               className={`rounded-3xl border p-4 ${
                 b.earned ? "border-brand/40 bg-brand/5" : "border-white/10 bg-panel"}`}>
            <div className="flex items-center gap-2">
              <span className={`inline-flex h-9 w-9 items-center justify-center rounded-2xl ${
                b.earned ? "bg-brand/20 text-brand" : "bg-white/5 text-ink-soft"}`}>
                {b.earned ? <Award className="h-4 w-4" /> : <Lock className="h-3.5 w-3.5" />}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm text-ink">{b.name}</p>
                <p className="text-[11px] text-ink-soft">
                  {b.earned ? `Earned ${(b.at || "").slice(0, 10)}` : `${b.have || 0} of ${b.goal}`}
                </p>
              </div>
              {b.earned && (
                <button
                  data-testid={`badge-share-${b.key}`}
                  onClick={() => share(b)}
                  aria-label={`Share ${b.name}`}
                  className="ms-auto shrink-0 rounded-full border border-brand/50 p-1.5 text-brand transition-colors hover:bg-brand/10"
                >
                  <Share2 className="h-3 w-3" />
                </button>
              )}
            </div>
            <p className="mt-2 text-xs text-ink-soft">{b.note}</p>
            {!b.earned && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                <span className="block h-full rounded-full bg-brand/60"
                      style={{ width: `${Math.min(100, ((b.have || 0) / b.goal) * 100)}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};
